import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Button from '@/components/school-management/Button/Button';
import Input from '@/components/school-management/Input/Input';
import styles from '@/components/school-management/SchoolDetailsPage/styles.module.scss';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import ClassCreatedModal from '@/components/school-management/SchoolDetailsPage/ClassCreatedModal';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { teacherService } from '@/school-management/services';
import { sanitizeEmail } from '@/admin/utils/validations';

interface TeacherFormData {
  firstName: string;
  lastName: string;
  email: string;
}

export default function CreateTeacherFormPage() {
  const navigate = useNavigate();
  const { schoolId } = useParams();
  const { user } = useAppSelector(authSelector);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('success');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { t } = useAdminTranslation();

  const schoolName = user?.metadata?.organizationDetails?.organizationName;
  const { control, handleSubmit, formState, watch } = useForm<TeacherFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
    },
  });

  const formValues = watch();

  // Check if form is valid for enabling buttons
  const isFormValid = useMemo(() => {
    return (
      formValues.firstName?.trim() &&
      formValues.lastName?.trim() &&
      formValues.email?.trim() &&
      formState.isValid
    );
  }, [formValues.firstName, formValues.lastName, formValues.email, formState.isValid]);

  const handleSuccessModalDone = () => {
    setShowSuccessModal(false);
    navigate(`/schools/${schoolId}?tab=teachers`, {
      replace: true,
    });
  };

  const onSubmit = async (values: TeacherFormData) => {
    const organizationId =
      user?.metadata?.organizationDetails?.organizationId || (schoolId as string) || '';
    const dealId = user?.metadata?.organizationDetails?.dealId || '';

    const teacherData = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
    };

    setIsSubmitting(true);

    try {
      const resp = await teacherService.createTeachers(organizationId, [teacherData], dealId);

      // Wait 2 seconds for DynamoDB stream to update postgres
      await new Promise(resolve => setTimeout(resolve, 2000));

      const isSuccess = resp?.data?.status === 'success';

      if (isSuccess) {
        setShowSuccessModal(true);
      } else {
        throw new Error(String(t('schoolDetailsPage.teachers.failedToCreate')));
      }
    } catch (e: any) {
      console.error('Error creating teacher:', e);
      setNotifMessage(t('settings.add_teachers.teacherCreationError') || '');
      setNotifType('error');
      setNotifOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.createClassPage}>
      <button className={styles.backButton} onClick={() => navigate(-1)}>
        ← {t('common.back')}
      </button>

      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{t('settings.add_teachers.add_teacher')}</h1>
        <span className={styles.schoolName}>{schoolName}</span>
      </div>

      <div className={styles.formCard}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.formFields}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t('studentDataModal.editForm.firstname')} *
              </label>
              <Controller
                name='firstName'
                control={control}
                rules={{
                  required: t('settings.add_teachers.errors.first_name_missing') || '',
                  maxLength: {
                    value: 30,
                    message: t('settings.add_teachers.errors.first_name_too_long'),
                  },
                  pattern: {
                    value: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
                    message: t('settings.add_teachers.errors.first_name_incorrect'),
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    placeholder={t('studentDataModal.editForm.firstname') || ''}
                    className={styles.formInput}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t('studentDataModal.editForm.lastname')} *
              </label>
              <Controller
                name='lastName'
                control={control}
                rules={{
                  required: t('settings.add_teachers.errors.last_name_missing') || '',
                  maxLength: {
                    value: 40,
                    message: t('settings.add_teachers.errors.last_name_too_long'),
                  },
                  pattern: {
                    value: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
                    message: t('settings.add_teachers.errors.last_name_incorrect'),
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    placeholder={t('studentDataModal.editForm.lastname') || ''}
                    className={styles.formInput}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('studentDataModal.editForm.email')} *</label>
              <Controller
                name='email'
                control={control}
                rules={{
                  required: t('settings.add_teachers.errors.email_incorrect') || '',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('settings.add_teachers.errors.email_incorrect'),
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    placeholder={t('studentDataModal.editForm.email') || ''}
                    className={styles.formInput}
                    type='email'
                    error={fieldState.error?.message}
                    onChange={e => {
                      const sanitizedValue = sanitizeEmail(e.target.value);
                      field.onChange(sanitizedValue);
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.actionButtons}>
            <button type='button' className={styles.cancelButton} onClick={() => navigate(-1)}>
              {t('common.cancel')}
            </button>
            <div className={styles.rightButtons}>
              <button
                type='submit'
                className={styles.continueButton}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? t('common.saving') : t('settings.add_teachers.create_teacher')}
              </button>
            </div>
          </div>
        </form>
      </div>

      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => {
          setNotifOpen(false);
        }}
      />

      <ClassCreatedModal
        show={showSuccessModal}
        addStudentsToNewGroup={false}
        onClose={handleSuccessModalDone}
        isTeacher={true}
      />
    </div>
  );
}
